import React, { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { generateScormPackage } from "@libertify/scorm";
import { Modal, Button } from "@libertify/ui";
import { Form, Input, InputNumber, Switch, message } from "antd";
import styled from "styled-components";

interface ScormExportModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  publicationId: string;
  experienceTitle: string;
  /** Fully-resolved public URL for the experience (the same one the share modal shows). */
  playerLaunchUrl: string;
}

const DEFAULT_THRESHOLD_PERCENT = 100;

function slugify(value: string): string {
  // Single-pass slug to avoid regex backtracking entirely (Sonar S5852).
  // Collapses runs of non-alphanumerics into single dashes, strips leading
  // and trailing dashes, caps at 80 chars.
  const chars: string[] = [];
  for (const ch of value.trim().toLowerCase()) {
    if ((ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9")) {
      chars.push(ch);
    } else if (chars.length > 0 && chars.at(-1) !== "-") {
      chars.push("-");
    }
  }
  while (chars.length > 0 && chars.at(-1) === "-") chars.pop();
  return chars.slice(0, 80).join("") || "libertify-scorm";
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick so the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const ScormExportModal: React.FC<ScormExportModalProps> = ({
  open,
  onClose,
  projectId,
  publicationId,
  experienceTitle,
  playerLaunchUrl,
}) => {
  const t = useTranslate();
  const [moduleTitle, setModuleTitle] = useState(experienceTitle);
  const [allowSeek, setAllowSeek] = useState(false);
  // Off by default: the player's standard behavior is to keep playing when
  // the learner switches tab/window.
  const [pauseOnBlur, setPauseOnBlur] = useState(false);
  const [threshold, setThreshold] = useState<number>(DEFAULT_THRESHOLD_PERCENT);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!playerLaunchUrl) {
      message.error(t("scorm.export.error"));
      return;
    }
    setSubmitting(true);
    try {
      const blob = await generateScormPackage({
        experience: {
          projectId,
          publicationId,
          title: experienceTitle,
        },
        playerLaunchUrl,
        options: {
          moduleTitle: moduleTitle || experienceTitle,
          allowSeek,
          pauseOnBlur,
          completionThreshold: Math.min(Math.max(threshold, 1), 100) / 100,
        },
      });
      downloadBlob(
        blob,
        `${slugify(moduleTitle || experienceTitle)}-scorm.zip`
      );
      message.success(t("scorm.export.success"));
      onClose();
    } catch (error) {
      console.error("SCORM export failed", error);
      message.error(t("scorm.export.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledScormModal
      open={open}
      onClose={onClose}
      title={t("scorm.export.title")}
      size="medium"
      zIndex={1200}
      footer={
        <StyledFooter>
          <Button variant="secondary" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConfirm()}
            loading={submitting}
          >
            {t("scorm.export.submit")}
          </Button>
        </StyledFooter>
      }
    >
      <StyledForm layout="vertical">
        <Form.Item
          label={t("scorm.export.moduleTitle")}
          help={t("scorm.export.moduleTitleHelp")}
        >
          <Input
            value={moduleTitle}
            onChange={(e) => setModuleTitle(e.target.value)}
            maxLength={120}
          />
        </Form.Item>
        <Form.Item help={t("scorm.export.allowSeekHelp")}>
          <StyledSwitchRow>
            <span>{t("scorm.export.allowSeek")}</span>
            <Switch checked={allowSeek} onChange={setAllowSeek} />
          </StyledSwitchRow>
        </Form.Item>
        <Form.Item help={t("scorm.export.pauseOnBlurHelp")}>
          <StyledSwitchRow>
            <span>{t("scorm.export.pauseOnBlur")}</span>
            <Switch checked={pauseOnBlur} onChange={setPauseOnBlur} />
          </StyledSwitchRow>
        </Form.Item>
        <Form.Item
          label={t("scorm.export.threshold")}
          help={t("scorm.export.thresholdHelp")}
        >
          <StyledThresholdRow>
            <InputNumber
              min={1}
              max={100}
              step={5}
              value={threshold}
              onChange={(value) =>
                setThreshold(
                  typeof value === "number" ? value : DEFAULT_THRESHOLD_PERCENT
                )
              }
              style={{ width: 120 }}
            />
            <span>{"%"}</span>
          </StyledThresholdRow>
        </Form.Item>
      </StyledForm>
    </StyledScormModal>
  );
};

// ─── Styled components ───

/* className lands on the antd modal root (a portal), which holds the title,
   body and footer — Inter for everything, including elements like the title
   and buttons that set their own font-family from the theme token. */
const StyledScormModal = styled(Modal)`
  &,
  .ant-modal-title,
  .ant-btn {
    font-family: "Inter", sans-serif;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-family: "Inter", sans-serif;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  /* Spacing comes from the flex gap so items with multi-line help text
     don't eat into the next field's default bottom margin. */
  .ant-form-item {
    margin-bottom: 0;
  }

  /* Inter everywhere — antd components set their own font-family, so the
     descendant override is required, not just inheritance from the root. */
  &,
  & * {
    font-family: "Inter", sans-serif;
  }
`;

const StyledSwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledThresholdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
